/**
 * AI Service - OpenAI Integration for Canvas Shape Generation
 * 
 * This service communicates with the Firebase Cloud Function
 * to generate canvas shapes using OpenAI GPT-4.
 */

// Cloud Function URL - Update this after deploying your function
const CLOUD_FUNCTION_URL = 
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:5001/collab-canvas-f20e2/us-central1/generateCanvasShapes'
    : 'https://us-central1-collab-canvas-f20e2.cloudfunctions.net/generateCanvasShapes';

/**
 * Generate canvas shapes from a natural language prompt
 * 
 * @param {string} prompt - Natural language description of what to create
 * @param {Array} currentShapes - Existing shapes on the canvas
 * @param {Object} canvasSize - Canvas dimensions
 * @returns {Promise<Object>} - { success, shapes, explanation, shapeCount }
 */
export async function generateShapesFromPrompt(prompt, currentShapes = [], canvasSize = { width: 5000, height: 5000 }) {
  try {
    console.log('🤖 Calling AI agent with prompt:', prompt);
    
    const response = await fetch(CLOUD_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt,
        currentShapes,
        canvasSize
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('✅ AI generated:', data.shapeCount, 'shapes');
    
    return data;
  } catch (error) {
    console.error('❌ AI generation error:', error);
    
    // Provide user-friendly error messages
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      throw new Error('Cannot connect to AI service. Check your internet connection.');
    } else if (error.message.includes('401')) {
      throw new Error('Authentication failed. Please log in again.');
    } else if (error.message.includes('429')) {
      throw new Error('Too many requests. Please wait a moment and try again.');
    } else if (error.message.includes('503')) {
      throw new Error('AI service is temporarily unavailable. Please try again later.');
    } else {
      throw new Error(error.message || 'Failed to generate shapes. Please try again.');
    }
  }
}

/**
 * Check if AI service is available
 * 
 * @returns {Promise<boolean>}
 */
export async function checkAIServiceHealth() {
  try {
    const healthUrl = CLOUD_FUNCTION_URL.replace('generateCanvasShapes', 'healthCheck');
    const response = await fetch(healthUrl);
    const data = await response.json();
    return data.status === 'healthy' && data.openai_configured;
  } catch (error) {
    console.error('Health check failed:', error);
    return false;
  }
}

